import React, { useMemo, useState } from 'react';
import {
  useAccount,
  useReadContract,
  useChainId,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { isAddress, zeroAddress, type Address } from 'viem';
import { FactoryChainBanner } from '../components/FactoryChainBanner';
import { factoryAbi } from '../abi/factory';
import { getFactoryAddress } from '../config/factories';
import { brand } from '../config/brand';
import type { ChainFamily, OwnerMode, PayeeRow } from '../split/types';
import { addressPlaceholder, bpsToPercentLabel, defaultRows, emptyRow, parseFeeBps, validateShares } from '../split/validation';
// Solana / Sui create-split paths hidden until deploy — restore:
// import { connectSolanaWallet, getSolanaAddress, connectSuiWallet, getSuiAddress } from '../split/walletConnect';
// import { SolanaDeployPanel } from '../components/SolanaDeployPanel';
// import { SuiDeployPanel } from '../components/SuiDeployPanel';

export function NewSplitter() {
  const chainFamily: ChainFamily = 'evm';
  // const [chainFamily, setChainFamily] = useState<ChainFamily>('evm');
  // const [nonEvmAddress, setNonEvmAddress] = useState<string | null>(null);
  // const [connecting, setConnecting] = useState(false);

  const [rows, setRows] = useState<PayeeRow[]>(defaultRows);
  const [advancedFee, setAdvancedFee] = useState(false);
  const [feeBpsInput, setFeeBpsInput] = useState('100');
  const [ownerMode, setOwnerMode] = useState<OwnerMode>('deployer');
  const [customOwner, setCustomOwner] = useState('');
  const [error, setError] = useState<string | null>(null);

  // const refreshNonEvmAddress = useCallback(async (family: ChainFamily) => { ... }, []);
  // useEffect(() => { refreshNonEvmAddress(chainFamily); }, [chainFamily, refreshNonEvmAddress]);
  // const handleChainFamilySwitch = async (family: ChainFamily) => { ... };

  const feeBpsParsed = useMemo(
    () => parseFeeBps(advancedFee ? feeBpsInput : '100'),
    [advancedFee, feeBpsInput]
  );
  const feePercentLabel = useMemo(
    () => bpsToPercentLabel(feeBpsParsed ?? BigInt(100)),
    [feeBpsParsed]
  );
  const { shareSum, shareSumValid, includedPayeeCount, shareRowsIncomplete } = useMemo(
    () => validateShares(rows, chainFamily),
    [rows, chainFamily]
  );

  const updateRow = (i: number, patch: Partial<PayeeRow>) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (i: number) => setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, j) => j !== i)));

  const placeholder = addressPlaceholder(chainFamily);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Create split</h1>
        <p className="text-sm opacity-70 mt-1">
          Configure payees with integer shares that total 100 (percent points). On each native or token claim, the
          protocol fee applies.
        </p>
      </div>

      {/* Chain family selector — Solana/Sui hidden until deploy
      <div className="space-y-2">...</div>
      */}

      {/* Owner mode (EVM-only while non-EVM chains are hidden) */}
      <div className="form-control max-w-md">
        <label className="label py-0">
          <span className="label-text text-xs opacity-70">Contract owner after deploy</span>
        </label>
        <select
          className="select select-bordered select-sm w-full"
          value={ownerMode}
          onChange={(e) => setOwnerMode(e.target.value as OwnerMode)}
        >
          <option value="none">None (renounce)</option>
          <option value="deployer">Deployer (connected wallet)</option>
          <option value="custom">Custom address</option>
        </select>
      </div>

      {/* Solana-only note (hidden with non-EVM)
      {chainFamily === 'solana' && (...)}
      */}

      {ownerMode === 'custom' && (
        <label className="form-control w-full max-w-xl">
          <span className="label-text text-xs opacity-70">Custom owner address</span>
          <input
            type="text"
            className="input input-bordered input-sm w-full font-mono"
            placeholder={placeholder}
            value={customOwner}
            onChange={(e) => setCustomOwner(e.target.value)}
          />
        </label>
      )}

      {/* Protocol fee */}
      <div className="form-control max-w-xl">
        <p className="text-sm">Protocol fee {feePercentLabel}</p>
        <div className="pt-2">
          <button
            type="button"
            className="btn btn-ghost btn-xs px-0 min-h-0 h-auto opacity-70 font-normal normal-case underline underline-offset-2"
            onClick={() => {
              setAdvancedFee((v) => {
                const next = !v;
                if (!next) setFeeBpsInput('100');
                return next;
              });
            }}
          >
            Customize protocol fee
          </button>
        </div>
        {advancedFee && (
          <div className="pt-2">
            <label className="form-control max-w-xs">
              <span className="label-text text-xs opacity-70">Custom protocol fee (basis points)</span>
              <input
                type="text"
                inputMode="numeric"
                className="input input-bordered input-sm font-mono w-full"
                value={feeBpsInput}
                onChange={(e) => setFeeBpsInput(e.target.value.replace(/[^\d]/g, ''))}
              />
            </label>
            <p className="text-xs opacity-70 mt-1">Minimum 100 bps (1%).</p>
          </div>
        )}
      </div>

      {/* Payee rows */}
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="flex flex-wrap gap-2 items-end">
            <label className="form-control flex-1 min-w-[200px]">
              <span className="label-text text-xs opacity-70">Payee address</span>
              <input
                type="text"
                className="input input-bordered input-sm w-full font-mono"
                placeholder={placeholder}
                value={row.address}
                onChange={(e) => updateRow(i, { address: e.target.value })}
              />
            </label>
            <label className="form-control w-32">
              <span className="label-text text-xs opacity-70">Share (%)</span>
              <input
                type="text"
                inputMode="numeric"
                className="input input-bordered input-sm w-full font-mono"
                value={row.shares}
                onChange={(e) => updateRow(i, { shares: e.target.value.replace(/[^\d]/g, '') })}
              />
            </label>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeRow(i)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <p className="text-sm">
        <span className="opacity-70">Total shares: </span>
        <span
          className={`font-mono font-medium ${
            shareSum !== BigInt(100) ? 'text-error' : shareSumValid ? 'text-success' : 'text-warning'
          }`}
        >
          {shareSum.toString()} / 100
        </span>
        {shareRowsIncomplete && (
          <span className="text-warning text-sm ml-2">(complete every payee row or clear unused rows)</span>
        )}
        {!shareRowsIncomplete && !shareSumValid && includedPayeeCount > 0 && (
          <span className="text-warning text-sm ml-2">(must equal 100)</span>
        )}
      </p>

      <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}>
        Add payee
      </button>

      {/* Chain-specific deploy panel */}
      <EvmDeployPanel
        rows={rows}
        feeBpsParsed={feeBpsParsed}
        ownerMode={ownerMode}
        customOwner={customOwner}
        shareSumValid={shareSumValid}
        shareRowsIncomplete={shareRowsIncomplete}
        includedPayeeCount={includedPayeeCount}
        error={error}
        setError={setError}
      />
      {/* {chainFamily === 'solana' && <SolanaDeployPanel ... />}
      {chainFamily === 'sui' && <SuiDeployPanel ... />} */}
    </div>
  );
}

/* ---------- EVM deploy panel (extracted from the old monolithic component) ---------- */

type DeployPanelProps = {
  rows: PayeeRow[];
  feeBpsParsed: bigint | null;
  ownerMode: OwnerMode;
  customOwner: string;
  shareSumValid: boolean;
  shareRowsIncomplete: boolean;
  includedPayeeCount: number;
  error: string | null;
  setError: (e: string | null) => void;
};

function EvmDeployPanel({
  rows,
  feeBpsParsed,
  ownerMode,
  customOwner,
  shareSumValid,
  shareRowsIncomplete,
  includedPayeeCount,
  error,
  setError,
}: DeployPanelProps) {
  const { openConnectModal } = useConnectModal();
  const { address: connectedAddress, isConnected } = useAccount();
  const chainId = useChainId();
  const factory = getFactoryAddress(chainId);
  const disconnectedDefaultChainId = brand.id === 'pgsplitter' ? 369 : 1;
  const disconnectedDefaultFactory =
    brand.id === 'pgsplitter'
      ? ('0x05c85D3d60847041e4B262059DFB1a85eBf40A2A' as Address)
      : getFactoryAddress(disconnectedDefaultChainId);
  const bannerChainId = isConnected ? chainId : disconnectedDefaultChainId;
  const bannerFactory = isConnected ? factory : disconnectedDefaultFactory;

  const { data: minFeeBps } = useReadContract({
    address: factory,
    abi: factoryAbi,
    functionName: 'MIN_PROTOCOL_FEE_BPS',
    query: { enabled: Boolean(factory) },
  });

  const { writeContract, data: hash, error: writeError, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash });

  const uiMinFeeBps = BigInt(100); // Enforce 1% minimum in UI.
  const minAllowedFeeBps =
    minFeeBps !== undefined && minFeeBps > uiMinFeeBps ? minFeeBps : uiMinFeeBps;
  const feeValid =
    feeBpsParsed !== null &&
    minFeeBps !== undefined &&
    feeBpsParsed >= minAllowedFeeBps &&
    feeBpsParsed <= BigInt(10_000);

  const ownerReady = useMemo(() => {
    if (ownerMode === 'none') return true;
    if (ownerMode === 'deployer') return Boolean(connectedAddress);
    const c = customOwner.trim();
    return isAddress(c) && c.toLowerCase() !== zeroAddress.toLowerCase();
  }, [ownerMode, connectedAddress, customOwner]);

  const formValid = feeValid && shareSumValid && includedPayeeCount > 0 && ownerReady;

  const resolveOwnerArg = (): Address | null => {
    if (ownerMode === 'none') return zeroAddress;
    if (ownerMode === 'deployer') return connectedAddress ?? null;
    const c = customOwner.trim();
    if (!isAddress(c) || c.toLowerCase() === zeroAddress.toLowerCase()) return null;
    return c as Address;
  };

  const deploy = () => {
    setError(null);
    if (!factory) {
      setError('Factory not configured for this chain.');
      return;
    }
    if (minFeeBps === undefined || feeBpsParsed === null || !feeValid) {
      const lo = minAllowedFeeBps.toString();
      setError(`Protocol fee must be between ${lo} and 10000 basis points (100%).`);
      return;
    }
    if (shareRowsIncomplete || !shareSumValid || includedPayeeCount === 0) {
      setError('Each payee row needs both address and share; integer shares must total exactly 100.');
      return;
    }
    const ownerArg = resolveOwnerArg();
    if (ownerArg === null) {
      setError(
        ownerMode === 'deployer'
          ? 'Connect your wallet to set owner to deployer.'
          : 'Enter a valid non-zero custom owner address.'
      );
      return;
    }

    const payees: Address[] = [];
    const shares: bigint[] = [];
    for (const r of rows) {
      const a = r.address.trim();
      const s = r.shares.trim();
      if (!a && !s) continue;
      if (!isAddress(a)) {
        setError(`Invalid address: ${a}`);
        return;
      }
      const n = BigInt(s || '0');
      if (n <= BigInt(0)) {
        setError('Each payee needs a positive integer share.');
        return;
      }
      payees.push(a as Address);
      shares.push(n);
    }
    if (payees.length === 0) {
      setError('Add at least one payee with address and shares.');
      return;
    }
    let sum = BigInt(0);
    for (const sh of shares) sum += sh;
    if (sum !== BigInt(100)) {
      setError('Shares must add up to 100 (percent points).');
      return;
    }

    writeContract({
      address: factory,
      abi: factoryAbi,
      functionName: 'createSplitter',
      args: [payees, shares, feeBpsParsed, ownerArg],
    });
  };

  return (
    <div className="space-y-4">
      <FactoryChainBanner chainId={bannerChainId} factoryAddress={bannerFactory} />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-primary"
          disabled={
            isConnected && (!factory || minFeeBps === undefined || !formValid || isPending || isConfirming)
          }
          onClick={() => {
            if (!isConnected) {
              openConnectModal?.();
              return;
            }
            deploy();
          }}
        >
          {!isConnected
            ? 'Connect wallet to deploy'
            : isConfirming
              ? 'Creating Split…'
              : isPending
                ? 'Confirm in wallet…'
                : 'Deploy split'}
        </button>
      </div>

      {error && <p className="text-sm text-error">{error}</p>}
      {writeError && <p className="text-sm text-error break-all">{writeError.message}</p>}
      {isSuccess && hash && (
        <p className="text-sm text-success break-all">
          Deployed. Transaction: {hash}. You can open the dashboard after the block confirms.
        </p>
      )}
    </div>
  );
}
